import { useEffect, useMemo, useRef, useState } from "react";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import NumberInput from "@shared/v2/NumberInput";
import TextButton from "@shared/v2/TextButton";
import Tooltip from "@shared/v2/Tooltip";
import Typography from "@shared/v2/Typography";
import {
  ArrowRightSolidV6,
  CheckSolidV6,
  CircleInfoSolidV6,
  TriangleExclamationSolidV6,
} from "@shared/v2/Icomoon";
import SaveProgressButton from "./SaveProgressButton";
import { useWizardContext } from "./WizardContext";
import { readScopedWizardValue, saveScopedWizardValue } from "../../services/wizardStorage";

const SOURCE_FIELDS = [
  {
    key: "pastClientsSphereReferrals",
    label: "Past Clients, Sphere, Referrals from Past Clients & Sphere",
  },
  { key: "openHouses", label: "Open Houses" },
  { key: "doorKnocking", label: "Door Knocking" },
  { key: "canceledExpired", label: "Canceled/Expired" },
  { key: "fsbo", label: "FSBO" },
  { key: "circleProspecting", label: "Circle Prospecting" },
  {
    key: "brivityWebLeadsSignCallsTeamLeads",
    label: "Brivity/Web Leads, Sign Calls, Team Leads",
  },
  {
    key: "agentReferrals",
    label: "Agent Referrals",
    tooltip: "Assigning units to this source currently does not impact Activity Goals (next step)",
  },
];

const createDefaultSources = () =>
  SOURCE_FIELDS.reduce((acc, field) => {
    acc[field.key] = 0;
    return acc;
  }, {});

const getInitialSources = (agentId, year, goals) => {
  const scopedSources = readScopedWizardValue(agentId, year, "unitSources");
  return {
    ...createDefaultSources(),
    ...(scopedSources?.modified ? scopedSources.values : goals.unitSources || {}),
  };
};

const UnitSources = () => {
  const {
    goToPage,
    year,
    agentId,
    saveProgress,
    wizardData: { goals, actions },
  } = useWizardContext();

  const closedUnits = Number(goals.closedUnits) || 0;
  const [unitSources, setUnitSources] = useState(() => getInitialSources(agentId, year, goals));
  const unitSourcesRef = useRef(unitSources);

  useEffect(() => {
    const nextSources = getInitialSources(agentId, year, goals);
    unitSourcesRef.current = nextSources;
    setUnitSources(nextSources);
  }, [agentId, goals.unitSources, year]);

  const totalAssigned = useMemo(
    () => Object.values(unitSources).reduce((sum, value) => sum + (Number(value) || 0), 0),
    [unitSources],
  );

  const isAssignedComplete = totalAssigned === closedUnits;
  const isResetDisabled = Object.values(unitSources).every((value) => Number(value || 0) === 0);

  const rowClassNameFor = (index) => {
    if (index === 0) return "[&>td]:tw-pb-[4px] [&>td]:tw-pt-[8px]";
    return "[&>td]:tw-py-[4px]";
  };

  const onSourceChange = (key) => (e) => {
    const parsed = Number(e.target.value);
    const nextSources = {
      ...unitSourcesRef.current,
      [key]: Number.isFinite(parsed) ? parsed : 0,
    };
    unitSourcesRef.current = nextSources;
    setUnitSources(nextSources);
    saveScopedWizardValue(agentId, year, "unitSources", nextSources, true);
  };

  const resetSources = () => {
    const defaults = createDefaultSources();
    unitSourcesRef.current = defaults;
    setUnitSources(defaults);
    saveScopedWizardValue(agentId, year, "unitSources", defaults, true);
  };

  const persistSources = ({ nextPage } = {}) => {
    const goalsToSave = {
      ...goals,
      unitSources,
      closedUnitsAssigned: totalAssigned,
    };
    saveScopedWizardValue(agentId, year, "unitSources", unitSources, true);
    actions?.setLocalGoals(goalsToSave);
    saveProgress({ nextPage, nextGoals: goalsToSave });
  };

  return (
    <>
      <Modal.Header
        title={
          <>
            {`${year} Business Planning`} <span className="tw-text-12d tw-text-gray-50">Step 2 of 5</span>
          </>
        }
        closeButton
      />
      <Modal.Body className="tw-flex tw-flex-col tw-gap-[16px] tw-overflow-y-auto">
        <div className="tw-flex tw-flex-col tw-gap-[4px]">
          <Typography variant="subtitle-m-bold">Where do you want your business to come from?</Typography>
          <Typography variant="body-s" className="!tw-text-gray-50">
            Determine which sources you want your <b>{goals.closedUnits} closed units</b> to come from in{" "}
            {year}. This will help us set up your Activity Goals.
          </Typography>
        </div>
        <table>
          <thead>
            <tr className="[&>th]:tw-py-[8px] tw-text-12d tw-text-gray-50 tw-whitespace-nowrap tw-border-solid tw-border-[1px] tw-border-gray-30 tw-border-x-0">
              <th className="tw-pr-[4px]">Source</th>
              <th className="tw-px-[4px] tw-font-semibold tw-min-w-[156px] tw-text-center">
                Units to Close in {year}
              </th>
              <th className="tw-pl-[4px] tw-font-semibold tw-min-w-[80px]">% of Business</th>
            </tr>
          </thead>
          <tbody className="[&>tr:nth-child(even)]:tw-bg-gray-5">
            {SOURCE_FIELDS.map(({ key, label, tooltip }, index) => (
              <tr key={key} className={rowClassNameFor(index)}>
                <td className="tw-pr-[4px]">
                  {label}
                  {tooltip && (
                    <>
                      {" "}
                      <Tooltip
                        trigger={<CircleInfoSolidV6 />}
                        content={tooltip}
                        innerClassName="tw-max-w-[180px]"
                        multiline
                      />
                    </>
                  )}
                </td>
                <td className="tw-px-[4px]" align="center">
                  <NumberInput
                    value={Number(unitSources[key]) || 0}
                    onChange={onSourceChange(key)}
                    disableIncrement={totalAssigned >= closedUnits}
                    inputProps={{
                      min: 0,
                      max: closedUnits,
                      "aria-label": `${label} units to close in ${year}`,
                    }}
                  />
                </td>
                <td className="tw-pl-[4px]" align="center">
                  {closedUnits > 0 && Number(unitSources[key]) > 0
                    ? `${Math.round(((Number(unitSources[key]) || 0) / closedUnits) * 100)}%`
                    : "—"}
                </td>
              </tr>
            ))}
            <tr className="[&>td]:tw-py-[8px] tw-text-12d tw-font-semibold tw-text-gray-50 tw-whitespace-nowrap tw-border-solid tw-border-[1px] tw-border-gray-30 tw-border-x-0">
              <td>{` `}</td>
              <td align="center">
                <div
                  className={`tw-inline-flex tw-items-center tw-gap-[6px] tw-rounded-[4px] tw-px-[8px] tw-py-[4px] tw-font-normal ${isAssignedComplete ? "tw-bg-[#edfaf1] tw-text-[#177335]" : "tw-bg-semantic-red-5 tw-text-[#a81e19]"}`}
                >
                  {isAssignedComplete ? <CheckSolidV6 /> : <TriangleExclamationSolidV6 />}
                  <span>
                    {totalAssigned} / {closedUnits} units assigned
                  </span>
                </div>
              </td>
              <td>{` `}</td>
            </tr>
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button schema="tertiary" size="medium" onClick={() => goToPage(3)}>
          Back
        </Button>
        <div className="tw-flex tw-gap-[12px]">
          <TextButton onClick={resetSources} disabled={isResetDisabled}>
            Reset Values
          </TextButton>
          <SaveProgressButton onClick={() => persistSources()} />
          <Button
            size="medium"
            className="tw-inline-flex tw-items-center tw-gap-[6px]"
            onClick={() => persistSources({ nextPage: 5 })}
            disabled={!isAssignedComplete}
          >
            Next <ArrowRightSolidV6 />
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default UnitSources;
